import { useEffect } from 'react'

/**
 * Componente OffCanvas personalizado.
 * * Este componente muestra un panel lateral deslizable (usualmente desde la derecha)
 * con un fondo oscurecido (overlay). Gestiona automáticamente el bloqueo del scroll
 * del cuerpo del documento cuando está activo.
 * * @component
 * @param {Object} props - Propiedades del componente.
 * @param {boolean} props.isOpen - Indica si el panel está visible o no.
 * @param {Function} props.onClose - Función de callback que se ejecuta para cerrar el panel (al hacer clic en el overlay o en el botón de cierre).
 * @param {string} props.title - Título que se muestra en la cabecera del panel.
 * @param {React.ReactNode} props.children - Contenido que se renderizará dentro del cuerpo del panel.
 * @param {number} [props.zIndexOverlay=2000] - Nivel de profundidad (z-index) para el fondo oscurecido.
 * @param {number} [props.zIndexPanel=2100] - Nivel de profundidad (z-index) para el panel lateral.
 * @returns {JSX.Element} El componente OffCanvas.
 */
const OffCanvas = ({
  isOpen,
  onClose,
  title,
  children,
  zIndexOverlay = 2000, // mucho más alto que cualquier contenido
  zIndexPanel = 2100, // encima del overlay
}) => {
  // Bloquear scroll del body cuando está abierto
  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto'
    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  return (
    <>
      {/* Overlay */}
      <div
        className={`position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50 transition-opacity ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        style={{ zIndex: zIndexOverlay }}
        onClick={onClose}
      />

      {/* Offcanvas panel */}
      <div
        className={`offcanvas offcanvas-end ${isOpen ? 'show' : ''}`}
        tabIndex="-1"
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: zIndexPanel,
          position: 'fixed', // asegurar que siempre esté fijo
          top: 0,
          right: 0,
          height: '100vh',
        }}
      >
        {/* Header */}
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">{title}</h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={onClose}
          />
        </div>

        {/* Body */}
        <div
          className="offcanvas-body overflow-auto"
          style={{ maxHeight: 'calc(100vh - 56px)' }}
        >
          {children}
        </div>
      </div>
    </>
  )
}

export default OffCanvas
